import axios from 'axios'
import { toast } from 'react-toastify'
import _ from 'lodash'
import classNames from 'classnames'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { requestGetAllStudents } from '../../actions/communitiesActions'
import StudentsTable from '././components/StudentsTable'

import classes from './StudentsPage.module.scss'
import MembersSearchInput from './components/MembersSearchInput'
import FilterButton from './components/FilterButton'
import { deserialize } from '../../utility/serialization'
import LoadingSpinner from '../common/loader/legacy-loaders/LoadingSpinner'
import DetailsDrawer from './components/DetailsDrawer'
import { useOutletContext } from 'react-router-dom'
import TablePlaceholder from './components/TablePlaceholder'

const PER_PAGE = 25
const DEBOUNCE_DURATION = 400
const FILTER_OPTIONS = {
  ALL: 'ALL',
  ACTIVE: 'ACTIVE',
}

const StudentsPage = () => {
  const { loadedStudentsData } = useOutletContext()
  const abortController = useRef(null)
  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const [students, setStudents] = useState(loadedStudentsData?.students)
  const [pagination, setPagination] = useState(loadedStudentsData?.meta.pagination)
  const [searchText, setSearchText] = useState('')
  const [filterOption, setFilterOption] = useState(FILTER_OPTIONS.ALL)
  const [isLoading, setIsLoading] = useState(false)
  const [inspectedStudent, setInspectedStudent] = useState(null)

  const fetchStudents = useCallback(
    ({ page, search, active, append }) =>
      requestGetAllStudents(communityId, page, PER_PAGE, search, null, active, abortController.current?.signal)
        .then(response => {
          const data = deserialize(response.data)
          setStudents(prev => {
            if (append) { return [...prev, ...data.students] }
            return data.students
          })
          setPagination(data.meta.pagination)
        }),
    [communityId]
  )

  const updateStudents = useCallback(
    (params) => {
      setIsLoading(true)
      fetchStudents({
        page: 1,
        search: searchText,
        active: filterOption === FILTER_OPTIONS.ACTIVE,
        append: false,
        ...params,
      })
        .catch((res) => {
          if (!axios.isCancel(res)) {
            toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT })
          }
        })
        .finally(() => setIsLoading(false))
    },
    [fetchStudents, filterOption, searchText]
  )

  const fetchMoreStudents = () => {
    updateStudents({ page: pagination?.page + 1, append: true })
  }

  const debouncedSearch = useMemo(
    () => _.debounce((search) => updateStudents({ search }), DEBOUNCE_DURATION),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleSearch = (e) => {
    setSearchText(e.target.value)
    debouncedSearch(e.target.value)
  }

  const handleFilter = (option) => {
    setFilterOption(option)
    updateStudents({ active: option === FILTER_OPTIONS.ACTIVE })
  }

  const openViewDetails = (student) => {
    setInspectedStudent(student)
  }

  return (
    <main className={classes.wrapper}>
      <header className='py-3 d-flex align-items-center'>
        <div className={classes.searchWrapper}>
          <h4 className={classNames('font-weight-bold mb-0', classes.title)}>
            Students {!!pagination && <>({pagination.totalCount || 0})</>}
          </h4>
          <MembersSearchInput placeholder='Search for Students' value={searchText} onChange={handleSearch} />
          {isLoading && <LoadingSpinner classNames={{ wrapper: classes.loading }} />}
        </div>
        <div className={classes.filterOptions}>
          <FilterButton
            text='All'
            onClick={() => handleFilter(FILTER_OPTIONS.ALL)}
            isActive={filterOption === FILTER_OPTIONS.ALL}
          />
          <FilterButton
            text='Active'
            onClick={() => handleFilter(FILTER_OPTIONS.ACTIVE)}
            isActive={filterOption === FILTER_OPTIONS.ACTIVE}
          />
        </div>
      </header>
      {students?.length === 0 ? (
        <TablePlaceholder message='No Students' />
      ) : (
        <StudentsTable
          students={students}
          fetchData={fetchMoreStudents}
          onStudentClick={openViewDetails}
          pagination={pagination}
          inspectedStudentId={inspectedStudent?.id}
        />
      )}
      {!!inspectedStudent && (
        <DetailsDrawer student={inspectedStudent} onClose={() => setInspectedStudent(null)} />
      )}
    </main>
  )
}

export default StudentsPage
